// CampaignMode.js — Freestyle Run
// Separate page from Home.js trending flow.
// Only accessible when has_campaign_mode = true on the user.
// Client types a specific topic angle → same pipeline runs.

import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ThemeProvider, createTheme, CssBaseline, AppBar, Toolbar,
  Box, Typography, Button, CircularProgress, TextField,
  LinearProgress,
} from "@mui/material";
import api from "../api/api";
import { getEmailFromToken } from "./auth";

const theme = createTheme({
  palette: {
    mode: "dark",
    primary:    { main: "#6c7ef8" },
    secondary:  { main: "#34d399" },
    background: { default: "#0d0e14", paper: "#13151e" },
    text:       { primary: "#f0f2f7", secondary: "#b0b7ce" },
  },
  typography: { fontFamily: "'DM Sans', system-ui, sans-serif" },
});

const D = {
  bg:           "#0d0e14",
  surface:      "#13151e",
  surfaceUp:    "#191c28",
  border:       "rgba(255,255,255,0.07)",
  borderMed:    "rgba(255,255,255,0.11)",
  accent:       "#6c7ef8",
  accentSoft:   "rgba(108,126,248,0.10)",
  accentBorder: "rgba(108,126,248,0.25)",
  purple:       "#a37bf8",
  success:      "#34d399",
  successSoft:  "rgba(52,211,153,0.10)",
  successBorder:"rgba(52,211,153,0.25)",
  warn:         "#f59e0b",
  warnSoft:     "rgba(245,158,11,0.10)",
  danger:       "#f87171",
  dangerSoft:   "rgba(248,113,113,0.10)",
  text:         "#f0f2f7",
  textSec:      "#b0b7ce",
  textMuted:    "#6b7390",
  font:         "'DM Sans',sans-serif",
  fontDisplay:  "'Plus Jakarta Sans','DM Sans',sans-serif",
};

const G = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@600;700;800&family=DM+Sans:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');
  *, *::before, *::after { box-sizing: border-box; }
  body { background: #0d0e14; -webkit-font-smoothing: antialiased; }
  @keyframes fadeUp { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:translateY(0)} }
  @keyframes orbFloat1 { 0%,100%{transform:translate(0,0)} 50%{transform:translate(24px,-16px)} }
  @keyframes orbFloat2 { 0%,100%{transform:translate(0,0)} 50%{transform:translate(-18px,22px)} }
  @keyframes progressGlow { 0%,100%{box-shadow:0 0 8px rgba(108,126,248,0.4)} 50%{box-shadow:0 0 18px rgba(163,123,248,0.6)} }
  .cm-fade { animation: fadeUp 0.45s cubic-bezier(0.22,1,0.36,1) both; }
  .cm-fade-1 { animation-delay: 0.07s; }
  .cm-fade-2 { animation-delay: 0.14s; }
  .cm-fade-3 { animation-delay: 0.21s; }
  .orb1 {
    position:fixed; width:600px; height:600px; border-radius:50%; z-index:0; pointer-events:none;
    background:radial-gradient(circle,rgba(108,126,248,0.06) 0%,transparent 70%);
    top:-200px; left:-200px; animation:orbFloat1 16s ease-in-out infinite;
  }
  .orb2 {
    position:fixed; width:500px; height:500px; border-radius:50%; z-index:0; pointer-events:none;
    background:radial-gradient(circle,rgba(163,123,248,0.05) 0%,transparent 70%);
    bottom:-160px; right:-120px; animation:orbFloat2 20s ease-in-out infinite;
  }
`;

const MIN_CHARS = 40;
const MAX_CHARS = 150;
const POLL_MS   = 5000;

const STAGE_LABELS = [
  { key: "queued",   label: "Queued",               pct: 5  },
  { key: "running",  label: "Researching topic…",   pct: 30 },
  { key: "insights", label: "Extracting insights…", pct: 55 },
  { key: "writing",  label: "Generating content…",  pct: 80 },
  { key: "complete", label: "Complete",              pct: 100 },
];

function stageProgress(status, progress) {
  if (status === "complete") return { label: "Complete", pct: 100 };
  if (status === "failed")   return { label: "Failed", pct: 0 };
  if (status === "queued")   return { label: "Queued — waiting for worker…", pct: 5 };
  const pct = Math.max(10, Math.min(95, progress || 20));
  if (pct < 40) return { label: "Researching topic…", pct };
  if (pct < 70) return { label: "Extracting insights…", pct };
  return { label: "Generating content…", pct };
}

export default function CampaignMode() {
  const navigate  = useNavigate();
  const [allowed,      setAllowed]      = useState(null); // null=checking, true/false
  const [topic,        setTopic]        = useState("");
  const [error,        setError]        = useState("");
  const [submitting,   setSubmitting]   = useState(false);
  const [task,         setTask]         = useState(null);
  const [zipUrl,       setZipUrl]       = useState("");
  const [activeCount,  setActiveCount]  = useState(0); // running+queued jobs across all features
  const pollRef = useRef(null);

  const MAX_CONCURRENT = 1;

  async function checkActiveJobs() {
    try {
      const res = await api.get("/pipeline/tasks");
      const jobs = res.data?.tasks || [];
      // Only count Freestyle Run jobs — trend pipeline runs separately
      const active = jobs.filter(j =>
        j.source === "topic_studio" &&
        (j.status === "queued" || j.status === "running")
      ).length;
      setActiveCount(active);
      return active;
    } catch { return 0; }
  }

  // Check if user has campaign_mode enabled + load active count
  useEffect(() => {
    const token = localStorage.getItem("auth_token");
    if (!token) { navigate("/login", { replace: true }); return; }
    api.get("/auth/me")
      .then(res => {
        if (res.data?.has_campaign_mode) setAllowed(true);
        else setAllowed(false);
      })
      .catch(() => navigate("/login", { replace: true }));
    checkActiveJobs();
  }, [navigate]);

  // Polling
  const startPolling = useCallback((task_id) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get(`/pipeline/task/${task_id}`);
        const t = res.data;
        setTask(t);
        if (t.status === "complete") {
          clearInterval(pollRef.current);
          if (t.zip_url) setZipUrl(t.zip_url);
        }
        if (t.status === "failed" || t.status === "canceled") {
          clearInterval(pollRef.current);
        }
      } catch {
        // Keep polling on transient errors
      }
    }, POLL_MS);
  }, []);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const charCount   = topic.trim().length;
  const isOverLimit = charCount > MAX_CHARS;
  const isValid     = charCount >= MIN_CHARS && !isOverLimit;
  const charsNeeded = Math.max(0, MIN_CHARS - charCount);

  async function handleSubmit() {
    if (!isValid) return;
    setError("");
    // Re-check active jobs right before submit to catch races
    const current = await checkActiveJobs();
    if (current >= MAX_CONCURRENT) {
      setError("A Freestyle Run is already in progress. Wait for it to complete before starting another.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/pipeline/run-download", { topic: topic.trim(), source: "topic_studio" });
      const newTask = {
        task_id:  res.data.task_id,
        status:   res.data.status || "queued",
        progress: 0,
        topic:    topic.trim(),
      };
      setTask(newTask);
      setActiveCount(c => c + 1);
      startPolling(res.data.task_id);
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      if (detail.toLowerCase().includes("quota") || detail.toLowerCase().includes("limit")) {
        setError("Generation limit reached for this month.");
      } else if (err.response?.status === 429) {
        setError("A Freestyle Run is already in progress. Wait for it to complete.");
      } else {
        setError("Failed to start generation. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  function handleReset() {
    if (pollRef.current) clearInterval(pollRef.current);
    setTask(null);
    setZipUrl("");
    setTopic("");
    setError("");
  }

  // ── Loading / access check ────────────────────────────────────────────────
  if (allowed === null) {
    return (
      <ThemeProvider theme={theme}><CssBaseline />
        <Box sx={{ minHeight: "100vh", background: D.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <CircularProgress sx={{ color: D.accent }} />
        </Box>
      </ThemeProvider>
    );
  }

  if (allowed === false) {
    navigate("/home", { replace: true });
    return null;
  }

  const { label: stageLabel, pct: stagePct } = task
    ? stageProgress(task.status, task.progress)
    : { label: "", pct: 0 };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <style>{G}</style>
      <div className="orb1" />
      <div className="orb2" />

      <Box sx={{ minHeight: "100vh", background: D.bg, position: "relative", zIndex: 1 }}>

        {/* Nav */}
        <AppBar position="sticky" elevation={0} sx={{
          background: "rgba(13,14,20,0.85)",
          backdropFilter: "blur(16px)",
          borderBottom: `1px solid ${D.border}`,
          zIndex: 10,
        }}>
          <Toolbar sx={{ gap: 2, minHeight: "60px !important", px: { xs: 2, sm: 3 } }}>
            {/* Logo */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, cursor: "pointer" }} onClick={() => navigate("/home")}>
              <Box sx={{ width: 28, height: 28, borderRadius: "8px", background: `linear-gradient(135deg, ${D.accent} 0%, ${D.purple} 100%)`, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: D.fontDisplay, fontWeight: 800, fontSize: 14, color: "#fff", boxShadow: "0 4px 14px rgba(108,126,248,0.35)", animation: "logoGlow 3s ease infinite" }}>Z</Box>
              <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: "15px", color: D.text, letterSpacing: "-0.02em" }}>
                zenithpost<span style={{ color: D.accent }}>.ai</span>
              </Typography>
            </Box>
            {/* Divider */}
            <Box sx={{ width: "1px", height: 18, background: D.border }} />
            {/* Breadcrumb */}
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, background: D.accentSoft, border: `1px solid ${D.accentBorder}`, borderRadius: "99px", px: 1.5, py: 0.5 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: D.accent }} />
              <Typography sx={{ fontSize: "11px", fontWeight: 700, color: D.accent, fontFamily: "'JetBrains Mono',monospace", textTransform: "uppercase", letterSpacing: "0.07em" }}>
                Freestyle Run
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }} />
            <Button
              onClick={() => navigate("/home")}
              size="small"
              sx={{ color: D.textMuted, fontFamily: D.font, textTransform: "none", fontSize: "13px", border: `1px solid ${D.border}`, borderRadius: "8px", px: 1.5, py: 0.5, "&:hover": { color: D.text, borderColor: D.borderMed } }}
            >
              ← Back to Home
            </Button>
            <Typography sx={{ fontSize: "12px", color: D.textMuted, fontFamily: "'JetBrains Mono',monospace", display: { xs: "none", sm: "block" } }}>
              {getEmailFromToken()}
            </Typography>
          </Toolbar>
        </AppBar>

        <Box sx={{ maxWidth: 680, mx: "auto", px: { xs: 2, sm: 3 }, py: { xs: 4, sm: 6 } }}>

          {/* Header */}
          <Box className="cm-fade" sx={{ mb: 5 }}>
            <Box sx={{
              display: "inline-flex", alignItems: "center", gap: 1,
              background: D.accentSoft, border: `1px solid ${D.accentBorder}`,
              borderRadius: "99px", px: 1.5, py: 0.5, mb: 2,
            }}>
              <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: D.accent }} />
              <Typography sx={{ fontSize: "11px", fontWeight: 700, color: D.accent, fontFamily: D.font, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                Freestyle Run
              </Typography>
            </Box>
            <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 800, fontSize: { xs: "24px", sm: "30px" }, letterSpacing: "-0.03em", color: D.text, lineHeight: 1.2, mb: 1.5 }}>
              Generate content around any topic you choose
            </Typography>
            <Typography sx={{ fontSize: "15px", color: D.textSec, fontFamily: D.font, lineHeight: 1.7 }}>
              Type a topic and we generate a full content run around it.
            </Typography>
          </Box>

          {/* Input area — hidden while task is running */}
          {!task && (
            <Box className="cm-fade cm-fade-1" sx={{
              background: D.surface,
              border: `1px solid ${D.border}`,
              borderRadius: "20px",
              p: { xs: 2.5, sm: 3.5 },
              mb: 3,
              position: "relative",
              overflow: "hidden",
              transition: "border-color 0.2s",
              "&:hover": { borderColor: D.borderMed },
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0, left: "15%", right: "15%", height: "1px",
                background: "linear-gradient(90deg, transparent, rgba(108,126,248,0.35), transparent)",
              },
            }}>
              {/* Instruction */}
              <Typography sx={{ fontSize: "13px", color: D.textSec, fontFamily: D.font, mb: 2, lineHeight: 1.6 }}>
                Write a <strong style={{ color: D.text }}>specific topic angle</strong>.
              </Typography>

              {/* Examples */}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2.5 }}>
                {[
                  "How AI is changing customer service in 2026",
                  "Why most businesses lose leads before the proposal stage",
                  "The rise of short-form video in B2B marketing",
                ].map(ex => (
                  <Box
                    key={ex}
                    onClick={() => setTopic(ex)}
                    sx={{
                      fontSize: "11.5px", color: D.textSec, fontFamily: D.font,
                      background: D.surfaceUp, border: `1px solid ${D.border}`,
                      borderRadius: "8px", px: 1.25, py: 0.5, cursor: "pointer",
                      transition: "all 0.15s",
                      "&:hover": { borderColor: D.accentBorder, color: D.accent, background: D.accentSoft },
                    }}
                  >
                    "{ex}"
                  </Box>
                ))}
              </Box>

              {/* Text input */}
              <TextField
                multiline
                minRows={3}
                maxRows={6}
                fullWidth
                placeholder="e.g. How AI-powered anxiety collars are changing pet care in 2026"
                value={topic}
                onChange={e => { if (e.target.value.length <= MAX_CHARS + 10) setTopic(e.target.value); setError(""); }}
                inputProps={{ maxLength: MAX_CHARS }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    background: "rgba(255,255,255,0.025)",
                    fontFamily: D.font,
                    fontSize: "14.5px",
                    lineHeight: 1.6,
                    color: D.text,
                  },
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: isOverLimit ? D.danger : D.border },
                  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: isOverLimit ? D.danger : D.borderMed },
                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: isOverLimit ? D.danger : D.accent, borderWidth: "1.5px" },
                  "& textarea::placeholder": { color: D.textMuted, opacity: 1 },
                }}
              />

              {/* Char count / validation hint */}
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1.25 }}>
                <Typography sx={{
                  fontSize: "12px",
                  fontFamily: D.font,
                  color: isOverLimit ? D.danger : isValid ? D.success : charsNeeded > 20 ? D.textMuted : D.warn,
                }}>
                  {isOverLimit
                    ? `Too long — keep it under ${MAX_CHARS} characters`
                    : isValid
                      ? "✓ Good — specific enough to research"
                      : charsNeeded > 0
                        ? `${charsNeeded} more characters — be more specific`
                        : ""}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: isOverLimit ? D.danger : D.textMuted, fontFamily: D.font }}>
                  {charCount} / {MAX_CHARS}
                </Typography>
              </Box>

              {activeCount >= MAX_CONCURRENT && (
                <Box sx={{ mt: 1.5, p: 1.5, borderRadius: "10px", background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.22)" }}>
                  <Typography sx={{ fontSize: "12.5px", color: D.warn, fontFamily: D.font }}>
                    <strong>A Freestyle Run is already in progress</strong> — wait for it to finish before starting another.
                  </Typography>
                </Box>
              )}

              {error && (
                <Typography sx={{ fontSize: "13px", color: D.danger, fontFamily: D.font, mt: 1.5 }}>
                  {error}
                </Typography>
              )}

              {/* Submit */}
              <Button
                fullWidth
                onClick={handleSubmit}
                disabled={!isValid || submitting || activeCount >= MAX_CONCURRENT}
                sx={{
                  mt: 2.5,
                  py: 1.4,
                  borderRadius: "12px",
                  background: isValid
                    ? `linear-gradient(135deg, ${D.accent}, #8b7cf8)`
                    : "rgba(255,255,255,0.06)",
                  color: isValid ? "#fff" : D.textMuted,
                  fontFamily: D.font,
                  fontWeight: 700,
                  fontSize: "14.5px",
                  textTransform: "none",
                  letterSpacing: "0.01em",
                  transition: "all 0.2s",
                  "&:hover": { opacity: isValid ? 0.92 : 1 },
                  "&:disabled": { opacity: 0.5, color: D.textMuted },
                }}
              >
                {submitting
                  ? <CircularProgress size={18} sx={{ color: "#fff" }} />
                  : "Generate Campaign Content →"}
              </Button>
            </Box>
          )}

          {/* Progress card — shown while running */}
          {task && (
            <Box sx={{
              background: D.surface,
              border: `1px solid ${task.status === "complete" ? D.successBorder : task.status === "failed" ? "rgba(248,113,113,0.25)" : D.accentBorder}`,
              borderRadius: "16px",
              p: { xs: 2.5, sm: 3.5 },
              mb: 3,
            }}>
              {/* Topic label */}
              <Typography sx={{ fontSize: "11px", color: D.textMuted, fontFamily: D.font, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", mb: 1 }}>
                Topic
              </Typography>
              <Typography sx={{ fontSize: "14px", color: D.text, fontFamily: D.font, lineHeight: 1.6, mb: 3 }}>
                {task.topic || topic}
              </Typography>

              {task.status === "complete" ? (
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
                    <Box sx={{ width: 32, height: 32, borderRadius: "50%", background: D.successSoft, border: `1px solid ${D.successBorder}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <span style={{ fontSize: "14px" }}>✓</span>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: "14px", fontWeight: 700, color: D.success, fontFamily: D.font }}>Content ready</Typography>
                      <Typography sx={{ fontSize: "12px", color: D.textMuted, fontFamily: D.font }}>40+ pieces generated — social posts, blogs, newsletters, images</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1.5 }}>
                    {zipUrl && (
                      <Button
                        href={zipUrl}
                        download
                        component="a"
                        sx={{
                          flex: 1,
                          py: 1.25,
                          borderRadius: "10px",
                          background: `linear-gradient(135deg, ${D.success}, #22c55e)`,
                          color: "#0d0e14",
                          fontFamily: D.font,
                          fontWeight: 700,
                          fontSize: "14px",
                          textTransform: "none",
                          "&:hover": { opacity: 0.92 },
                        }}
                      >
                        ↓ Download ZIP
                      </Button>
                    )}
                    <Button
                      onClick={handleReset}
                      sx={{
                        flex: 1,
                        py: 1.25,
                        borderRadius: "10px",
                        border: `1px solid ${D.border}`,
                        color: D.textSec,
                        fontFamily: D.font,
                        fontWeight: 600,
                        fontSize: "14px",
                        textTransform: "none",
                        "&:hover": { borderColor: D.borderMed, color: D.text, background: D.surfaceUp },
                      }}
                    >
                      New Topic
                    </Button>
                  </Box>
                </Box>
              ) : task.status === "failed" ? (
                <Box>
                  <Typography sx={{ fontSize: "14px", color: D.danger, fontFamily: D.font, mb: 2 }}>
                    Generation failed — your quota has been refunded.
                  </Typography>
                  <Button
                    onClick={handleReset}
                    sx={{ color: D.accent, fontFamily: D.font, textTransform: "none", border: `1px solid ${D.accentBorder}`, borderRadius: "10px", px: 2.5 }}
                  >
                    Try again
                  </Button>
                </Box>
              ) : (
                <Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                    <Typography sx={{ fontSize: "13px", color: D.textSec, fontFamily: D.font }}>{stageLabel}</Typography>
                    <Typography sx={{ fontSize: "12px", color: D.textMuted, fontFamily: D.font }}>{stagePct}%</Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={stagePct}
                    sx={{
                      height: 5,
                      borderRadius: 99,
                      background: "rgba(255,255,255,0.06)",
                      "& .MuiLinearProgress-bar": {
                        background: `linear-gradient(90deg, ${D.accent}, ${D.purple})`,
                        borderRadius: 99,
                      },
                    }}
                  />
                  <Typography sx={{ fontSize: "12px", color: D.textMuted, fontFamily: D.font, mt: 1.5 }}>
                    This takes 3–7 minutes. You can leave this page and come back — check your Files dashboard.
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Info card */}
          {!task && (
            <Box className="cm-fade cm-fade-2" sx={{
              background: D.surfaceUp,
              border: `1px solid ${D.border}`,
              borderRadius: "16px",
              p: 2.5,
            }}>
              <Typography sx={{ fontSize: "12px", fontWeight: 700, color: D.textSec, fontFamily: D.font, mb: 1.5, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                What gets generated
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                {[
                  "10 Facebook posts", "10 Instagram posts",
                  "10 LinkedIn posts", "10 Twitter/X posts",
                  "10 Blog articles", "10 Newsletters",
                  "10 Generated images", "Citations & sources",
                ].map(item => (
                  <Box key={item} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 5, height: 5, borderRadius: "50%", background: D.accent, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: "12.5px", color: D.textSec, fontFamily: D.font }}>{item}</Typography>
                  </Box>
                ))}
              </Box>
              <Typography sx={{ fontSize: "12px", color: D.textMuted, fontFamily: D.font, mt: 1.5 }}>
                All active add-ons (Compliance, Intelligence Suite) also apply to Freestyle Runs.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
}
